import React from "react";
import { Asterisk, ChevronDown, ChevronRight, Store } from "lucide-react";

import { Tip } from "@/components/ui";
import type { FolderFlag } from "@/lib/leaves/leafRules";

interface FolderHeaderProps {
  folder: string;
  open: boolean;
  shown: number;
  required: number;
  perStore: number;
  onToggle: () => void;
  onSetAll: (flag: FolderFlag) => void;
}

const meta = (required: number, perStore: number) =>
  [
    required > 0 && `${required} required`,
    perStore > 0 && `${perStore} per store`,
  ]
    .filter(Boolean)
    .join(", ");

const actionClass =
  "inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-500 opacity-0 transition-opacity duration-150 hover:bg-slate-200 hover:text-slate-900 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40 group-hover:opacity-100 group-focus-within:opacity-100 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-100";

export const FolderHeader: React.FC<FolderHeaderProps> = ({
  folder,
  open,
  shown,
  required,
  perStore,
  onToggle,
  onSetAll,
}) => {
  const Chevron = open ? ChevronDown : ChevronRight;
  const note = meta(required, perStore);
  const actions: { flag: FolderFlag; name: string; icon: React.ElementType }[] =
    [
      { flag: "alwaysRequired", name: "Required", icon: Asterisk },
      { flag: "perStore", name: "Per store", icon: Store },
    ];
  return (
    <div className="group sticky top-[104px] z-[5] flex h-11 items-center border-t border-slate-200 bg-slate-50 pr-3 dark:border-slate-800 dark:bg-[#172135]">
      <button
        type="button"
        aria-expanded={open}
        onClick={onToggle}
        className="flex h-full min-w-0 flex-1 items-center gap-2 px-4 text-left text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-500/40"
      >
        <Chevron className="h-4 w-4 shrink-0 text-slate-400" />
        <span className="font-medium text-slate-900 dark:text-slate-100">
          {folder}
        </span>
        <span className="text-xs tabular-nums text-slate-500 dark:text-slate-400">
          {shown}
        </span>
        {note && (
          <span className="truncate text-xs text-slate-500 dark:text-slate-400">
            {note}
          </span>
        )}
      </button>
      <div className="flex items-center gap-1">
        {open &&
          actions.map(({ flag, name, icon: Icon }) => {
            const tip = `Set ${name} for all ${shown} leaves in ${folder}`;
            return (
              <Tip key={flag} label={tip}>
                <button
                  type="button"
                  aria-label={tip}
                  onClick={() => onSetAll(flag)}
                  className={actionClass}
                >
                  <Icon className="h-4 w-4" />
                </button>
              </Tip>
            );
          })}
      </div>
    </div>
  );
};
